import { BillingPeriodType } from 'api/BillingApi';
import { useDateContext } from 'modules/billing/contexts/DateContext';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import { CSSObjectWithLabel } from 'react-select';
import { FlexSpacer } from 'ui/flexSpacer';
import { Select } from 'ui/selectv2/Select';
import { DefaultTheme } from 'ui/selectv2/Themes';
import { BaseOption } from 'ui/selectv2/types';
import { spacing, Subtitle2 } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { BillingPeriod } from '../types/dates';

const CustomTheme = (errorMessage?: ReactNode) => {
  return {
    ...DefaultTheme(errorMessage),
    control: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      // select height does not equal button size MD or LG?
      minHeight: pxToRem(40),
      minWidth: spacing.x16,
    }),
  };
};

const createBillingPeriodOptions = (billingPeriods: BillingPeriod[] = []): BaseOption<string>[] => {
  return billingPeriods.map(({ endDatePST, isFutureDate, rangeText, startDatePST, type }) => ({
    id: endDatePST,
    isDisabled: isFutureDate,
    label: rangeText,
    Suffix: type === BillingPeriodType.Current ? 'current' : undefined,
    value: `${startDatePST}|${endDatePST}`,
  }));
};

export const BillingPeriodSelector: FC = () => {
  const { billingPeriods, billingPeriod, setDateRange } = useDateContext();
  const options = useMemo(() => createBillingPeriodOptions(billingPeriods), [billingPeriods]);
  const selectedValue = `${billingPeriod.startDatePST}|${billingPeriod.endDatePST}`;
  const value = useMemo(() => {
    const val = options.find((option) => option.value === selectedValue);
    return val ? [val] : [];
  }, [options, selectedValue]);

  const handleSelect = useCallback(
    ([{ value }]: BaseOption<string>[]) => {
      const parts = value.split('|');
      if (parts.length === 2) {
        const [start, end] = parts;
        setDateRange(start, end);
      }
    },
    [setDateRange],
  );

  return (
    <FlexSpacer>
      <Subtitle2>Billing period:</Subtitle2>
      <Select
        customTheme={CustomTheme}
        isOptionDisabled={(option) => !!option.isDisabled}
        onSelect={handleSelect}
        options={options}
        value={value}
      />
    </FlexSpacer>
  );
};
